import { useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import {
  Alert,
  Button,
  Calendar,
  Card,
  Col,
  DatePicker,
  Empty,
  Form,
  Input,
  List,
  message,
  Modal,
  Radio,
  Row,
  Select,
  Space,
  Spin,
  Tag,
  Tooltip,
  Tour,
  Typography,
} from "antd";
import {
  DeleteOutlined,
  EditOutlined,
  LeftOutlined,
  PlusOutlined,
  QuestionCircleOutlined,
  RightOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import PvTable from "../../components/Table/PvTable";
import Popup from "../../components/Popup";
import useAdaptiveCalendarLayout from "../../hooks/useAdaptiveCalendarLayout";
import usePrismCalendarNoteService from "../../services/Prism/calendarNoteService";
import usePrismSubscriptionService from "../../services/Prism/subscriptionService";
import {
  buildOfficeFilterOptions,
  canManageAnyPrismOffice,
  CRITICALITY_COLORS,
  CRITICALITY_LABELS,
  formatDate,
  formatLabel,
  formatOfficeName,
  formatSubOfficeName,
  getCriticality,
  getDateTime,
  getOfficeFilterValue,
  getPrismPriorityRank,
  isFullPrismRecord,
  LIMITED_PRISM_SUBSCRIPTION_FIELD_KEYS,
  renderRestrictedValue,
  STATUS_COLORS,
  STATUS_FILTER_OPTIONS,
} from "./prismSubscriptionUtils";
import {
  buildCalendarEvents,
  buildCalendarDateItems,
  CALENDAR_EVENT_TYPES,
  DEFAULT_NOTE_COLOR,
  getCalendarDateCellSummary,
  getCalendarWeekCount,
  getCalendarEventTypeClass as getEventTypeClass,
  getCalendarEventTypeLabel as getEventTypeLabel,
  getNoteColorClassName,
  getNoteColorLabel,
  groupCalendarEventsByDate,
  groupCalendarNotesByDate,
  normalizeNoteColor,
  NOTE_COLOR_OPTIONS,
  setCalendarMonthAndYear,
  sortCalendarEvents,
  sortCalendarNotes,
} from "./prismCalendarUtils";
import PrismTruncatedText from "./PrismTruncatedText";
import PrismCalendarLegend from "./PrismCalendarLegend";
import PrismSubscriptionDetailsModal from "./PrismSubscriptionDetailsModal";
import "../../styles/pages/prism/prismCalendar.scss";

const { Text, Title } = Typography;

const EVENT_TYPE_OPTIONS = Object.entries(CALENDAR_EVENT_TYPES).map(
  ([value, event]) => ({
    value,
    label: event.label,
  }),
);

const getCriticalityColor = (criticality) => {
  if (criticality === "overdue") return "#000";
  return CRITICALITY_COLORS[criticality] || null;
};

const renderCriticality = (criticality) => {
  if (!criticality) return "-";

  return (
    <Tag
      color={CRITICALITY_COLORS[criticality] || "default"}
      style={
        criticality === "overdue"
          ? { color: "#fff", borderColor: "#000" }
          : undefined
      }
    >
      {CRITICALITY_LABELS[criticality] || formatLabel(criticality)}
    </Tag>
  );
};

const renderStatus = (status) => {
  if (!status) return "-";

  return <Tag color={STATUS_COLORS[status] || "blue"}>{formatLabel(status)}</Tag>;
};

const getErrorMessage = (error, fallback) => {
  if (!error) return fallback;
  if (typeof error === "string") return error;

  const firstValidationMessage = Object.values(error?.errors || {})
    .flat()
    .find((value) => typeof value === "string");

  if (firstValidationMessage) return firstValidationMessage;
  if (typeof error?.message === "string") return error.message;

  return fallback;
};

const PrismCalendar = () => {
  const [noteForm] = Form.useForm();
  const [officeFilter, setOfficeFilter] = useState(null);
  const [statusFilter, setStatusFilter] = useState(null);
  const [eventTypeFilter, setEventTypeFilter] = useState(null);
  const [viewMode, setViewMode] = useState("month");
  const [currentCalendarDate, setCurrentCalendarDate] = useState(dayjs());
  const calendarWeekCount = getCalendarWeekCount(currentCalendarDate);
  const [isNoteModalOpen, setIsNoteModalOpen] = useState(false);
  const [selectedNote, setSelectedNote] = useState(null);
  const [detailsSubscription, setDetailsSubscription] = useState(null);
  const [noteSubmitError, setNoteSubmitError] = useState(null);
  const [isTourOpen, setIsTourOpen] = useState(false);
  const toolbarRef = useRef(null);
  const addNoteButtonRef = useRef(null);
  const calendarGridRef = useRef(null);
  const {
    calendarContainerRef,
    density: calendarDensity,
    visibleItemLimit,
  } = useAdaptiveCalendarLayout(calendarWeekCount);
  const { details = {} } = useSelector((state) => state.accountDetailSlice);
  const {
    getSubscriptions,
    subscriptionLoading,
    subscriptionResponse,
    subscriptionError,
  } = usePrismSubscriptionService();
  const {
    getCalendarNotes,
    calendarNoteLoading,
    calendarNoteResponse,
    calendarNoteError,
    createCalendarNote,
    createCalendarNoteLoading,
    updateCalendarNote,
    updateCalendarNoteLoading,
    deleteCalendarNote,
    deleteCalendarNoteLoading,
  } = usePrismCalendarNoteService();
  const canViewPrivilegedDetails = useMemo(
    () => canManageAnyPrismOffice(details),
    [details],
  );

  const tourSeenStorageKey = details?.email
    ? `prism-calendar-tour-seen:${details.email}`
    : null;

  useEffect(() => {
    if (!canViewPrivilegedDetails || !tourSeenStorageKey) return;

    try {
      if (!window.localStorage.getItem(tourSeenStorageKey)) {
        setIsTourOpen(true);
      }
    } catch {
      // localStorage can be unavailable (private browsing, etc.) — the tour
      // simply won't auto-open in that case, no need to fail loudly.
    }
  }, [canViewPrivilegedDetails, tourSeenStorageKey]);

  const handleTourClose = () => {
    setIsTourOpen(false);

    if (!tourSeenStorageKey) return;

    try {
      window.localStorage.setItem(tourSeenStorageKey, "1");
    } catch {
      // Ignore — worst case the tour auto-opens again next visit.
    }
  };

  const tourSteps = useMemo(
    () => [
      {
        title: "Navigate and filter",
        description:
          "Move between months, jump to Today, filter by office/status/event type, or switch between Month and List view.",
        target: () => toolbarRef.current,
      },
      {
        title: "Add a general note",
        description:
          "Add your own reminders tied to any date — not linked to a specific subscription.",
        target: () => addNoteButtonRef.current,
      },
      {
        title: "Click a date for details",
        description:
          "Click any date on the calendar to see everything happening that day — subscription renewals, endings, and your notes — in the panel on the right.",
        target: () => calendarGridRef.current,
      },
      {
        title: "Prefer a table?",
        description:
          "Switch to List View using the toggle above to see everything as a sortable table, plus a separate table of your general notes.",
      },
    ],
    [],
  );

  useEffect(() => {
    getSubscriptions().catch(() => {
      // useAxios stores the load error for the inline alert.
    });
    if (canViewPrivilegedDetails) {
      getCalendarNotes().catch(() => {
        // useAxios stores the load error for the inline alert.
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canViewPrivilegedDetails]);

  const subscriptions = useMemo(
    () =>
      Array.isArray(subscriptionResponse?.data)
        ? subscriptionResponse.data.filter((item) => item.is_enabled !== false)
        : [],
    [subscriptionResponse],
  );

  const calendarNotes = useMemo(
    () =>
      Array.isArray(calendarNoteResponse?.data)
        ? sortCalendarNotes(calendarNoteResponse.data)
        : [],
    [calendarNoteResponse],
  );

  const calendarNotesByDate = useMemo(
    () => groupCalendarNotesByDate(calendarNotes),
    [calendarNotes],
  );

  const officeFilterOptions = useMemo(
    () => buildOfficeFilterOptions(subscriptions),
    [subscriptions],
  );

  const filteredSubscriptions = useMemo(
    () =>
      subscriptions.filter((subscription) => {
        const officeMatches = officeFilter
          ? getOfficeFilterValue(subscription.subscriber_department) ===
            officeFilter
          : true;
        const statusMatches = statusFilter
          ? isFullPrismRecord(subscription, details) &&
            subscription.contract_status === statusFilter
          : true;

        return officeMatches && statusMatches;
      }),
    [subscriptions, officeFilter, statusFilter, details],
  );

  const tableData = useMemo(() => {
    const mappedSubscriptions = filteredSubscriptions.map(
      (subscription, index) => {
        const isFullRecord = isFullPrismRecord(subscription, details);

        return {
          originalIndex: index,
          key: subscription.id,
          ...subscription,
          criticality: isFullRecord
            ? getCriticality(
                subscription.contract_renewal_date,
                subscription.subscription_type,
                subscription.contract_status,
              )
            : null,
          isFullRecord,
        };
      },
    );

    return [...mappedSubscriptions].sort((a, b) => {
      const criticalityA = getPrismPriorityRank(a);
      const criticalityB = getPrismPriorityRank(b);

      if (criticalityA !== criticalityB) return criticalityA - criticalityB;

      const renewalDateA = getDateTime(a.contract_renewal_date);
      const renewalDateB = getDateTime(b.contract_renewal_date);

      if (renewalDateA !== renewalDateB) return renewalDateA - renewalDateB;

      const endDateA = getDateTime(a.contract_end_date);
      const endDateB = getDateTime(b.contract_end_date);

      if (endDateA !== endDateB) return endDateA - endDateB;

      const nameCompare = String(a.subscription_name || "").localeCompare(
        String(b.subscription_name || ""),
      );

      return nameCompare || a.originalIndex - b.originalIndex;
    });
  }, [filteredSubscriptions, details]);

  const calendarEvents = useMemo(
    () => buildCalendarEvents(filteredSubscriptions, details, eventTypeFilter),
    [filteredSubscriptions, details, eventTypeFilter],
  );

  const calendarEventsByDate = useMemo(
    () => groupCalendarEventsByDate(calendarEvents),
    [calendarEvents],
  );

  const selectedDateKey = currentCalendarDate.format("YYYY-MM-DD");
  const selectedDateEvents = useMemo(
    () => sortCalendarEvents(calendarEventsByDate[selectedDateKey] || []),
    [calendarEventsByDate, selectedDateKey],
  );
  const selectedDateNotes = calendarNotesByDate[selectedDateKey] || [];
  const selectedDateItems = buildCalendarDateItems(
    selectedDateNotes,
    selectedDateEvents,
  );

  const monthSummary = useMemo(() => {
    const monthKey = currentCalendarDate.format("YYYY-MM");
    const monthEvents = calendarEvents.filter((event) =>
      event.dateKey.startsWith(monthKey),
    );

    return {
      subscriptions: new Set(
        monthEvents.map((event) => event.subscription.id),
      ).size,
      renewals: monthEvents.filter((event) => event.types.includes("renewal"))
        .length,
      endings: monthEvents.filter((event) => event.types.includes("end")).length,
      notes: calendarNotes.filter((note) =>
        note.note_date.startsWith(monthKey),
      ).length,
    };
  }, [calendarEvents, calendarNotes, currentCalendarDate]);

  const handleAddNoteOpen = () => {
    setSelectedNote(null);
    setNoteSubmitError(null);
    noteForm.resetFields();
    noteForm.setFieldsValue({
      note_date: currentCalendarDate,
      color: DEFAULT_NOTE_COLOR,
    });
    setIsNoteModalOpen(true);
  };

  const handleEditNoteOpen = (note) => {
    setSelectedNote(note);
    setNoteSubmitError(null);
    noteForm.setFieldsValue({
      note_date: dayjs(note.note_date),
      title: note.title,
      note: note.note || "",
      color: normalizeNoteColor(note.color),
    });
    setIsNoteModalOpen(true);
  };

  const handleNoteCancel = () => {
    setIsNoteModalOpen(false);
    setSelectedNote(null);
    setNoteSubmitError(null);
    noteForm.resetFields();
  };

  const handleNoteSubmit = async (values) => {
    const payload = {
      note_date: values.note_date.format("YYYY-MM-DD"),
      title: values.title.trim(),
      note: values.note?.trim() || null,
      color: normalizeNoteColor(values.color),
    };

    try {
      setNoteSubmitError(null);

      if (selectedNote) {
        await updateCalendarNote(selectedNote.id, payload);
        message.success("General note updated successfully.");
      } else {
        await createCalendarNote(payload);
        message.success("General note added successfully.");
      }

      handleNoteCancel();
      getCalendarNotes().catch(() => {
        // useAxios stores the reload error for the inline alert.
      });
    } catch (error) {
      const submitError = error?.response?.data || error?.message;
      setNoteSubmitError(submitError);
      message.error(
        getErrorMessage(
          submitError,
          selectedNote
            ? "Failed to update general note."
            : "Failed to add general note.",
        ),
      );
    }
  };

  const handleDeleteNote = (note) => {
    setNoteSubmitError(null);
    Modal.confirm({
      title: "Delete General Note",
      content: `Are you sure you want to delete "${note.title}"?`,
      okText: "Delete",
      okButtonProps: { danger: true },
      cancelText: "Cancel",
      onOk: async () => {
        try {
          await deleteCalendarNote(note.id);
          message.success("General note deleted successfully.");
          getCalendarNotes().catch(() => {
            // useAxios stores the reload error for the inline alert.
          });
        } catch (error) {
          message.error(
            getErrorMessage(error?.response?.data || error?.message, "Failed to delete general note."),
          );
        }
      },
    });
  };

  const fullColumns = [
    {
      title: "Criticality",
      dataIndex: "criticality",
      key: "criticality",
      width: "12%",
      render: (value, record) =>
        renderRestrictedValue(record, value, details, renderCriticality),
    },
    {
      title: "Subscription Name",
      dataIndex: "subscription_name",
      key: "subscription_name",
      sortable: true,
      width: "18%",
      ellipsis: { showTitle: false },
      render: (value) => <PrismTruncatedText value={value} />,
    },
    {
      title: "Description",
      dataIndex: "subscription_details",
      key: "subscription_details",
      width: "18%",
      ellipsis: { showTitle: false },
      render: (value) => <PrismTruncatedText value={value} lines={2} />,
    },
    {
      title: "Office",
      dataIndex: "subscriber_department",
      key: "subscriber_department",
      sortable: true,
      width: "14%",
      ellipsis: { showTitle: false },
      render: (value) => (
        <PrismTruncatedText value={value} formatter={formatOfficeName} />
      ),
    },
    {
      title: "Unit",
      dataIndex: "subscriber_sub_department",
      key: "subscriber_sub_department",
      width: "14%",
      ellipsis: { showTitle: false },
      render: (value) => (
        <PrismTruncatedText value={value} formatter={formatSubOfficeName} />
      ),
    },
    {
      title: "Status",
      dataIndex: "contract_status",
      key: "contract_status",
      width: "10%",
      render: (value, record) =>
        renderRestrictedValue(record, value, details, renderStatus),
    },
    {
      title: "Renewal Date",
      dataIndex: "contract_renewal_date",
      key: "contract_renewal_date",
      sortable: true,
      width: "11%",
      render: (value, record) =>
        renderRestrictedValue(record, value, details, formatDate),
    },
    {
      title: "Contract End Date",
      dataIndex: "contract_end_date",
      key: "contract_end_date",
      sortable: true,
      width: "11%",
      render: (value, record) =>
        renderRestrictedValue(record, value, details, formatDate),
    },
    {
      title: "Renewal Frequency",
      dataIndex: "contract_renewal_frequency",
      key: "contract_renewal_frequency",
      width: "10%",
      render: (value, record) =>
        renderRestrictedValue(record, value, details, formatLabel),
    },
  ];
  const columns = canViewPrivilegedDetails
    ? fullColumns
    : fullColumns.filter((column) =>
        LIMITED_PRISM_SUBSCRIPTION_FIELD_KEYS.has(column.key),
      );

  const renderNoteColorTag = (color, label = getNoteColorLabel(color)) => (
    <Tag
      className={`prism-calendar-note-color-tag ${getNoteColorClassName(color)}`}
    >
      <span className="prism-calendar-note-color-swatch" aria-hidden="true" />
      {label}
    </Tag>
  );

  const calendarNoteColumns = [
    {
      title: "Date",
      dataIndex: "note_date",
      key: "note_date",
      width: "12%",
      render: formatDate,
    },
    {
      title: "Title",
      dataIndex: "title",
      key: "title",
      searchable: true,
      preserveRenderOnSearch: true,
      width: "20%",
      ellipsis: { showTitle: false },
      render: (value) => <PrismTruncatedText value={value} />,
    },
    {
      title: "Color",
      dataIndex: "color",
      key: "color",
      width: "12%",
      render: (value) => renderNoteColorTag(value),
    },
    {
      title: "Note",
      dataIndex: "note",
      key: "note",
      searchable: true,
      preserveRenderOnSearch: true,
      width: "38%",
      render: (value) => <PrismTruncatedText value={value} lines={2} />,
    },
    {
      title: "Actions",
      key: "actions",
      width: "18%",
      render: (_, note) => (
        <Space className="prism-calendar-notes-table__actions" size={4} wrap>
          <Button
            size="small"
            icon={<EditOutlined />}
            onClick={() => {
              setCurrentCalendarDate(dayjs(note.note_date));
              handleEditNoteOpen(note);
            }}
          >
            Edit
          </Button>
          <Button
            size="small"
            danger
            icon={<DeleteOutlined />}
            loading={deleteCalendarNoteLoading}
            onClick={() => handleDeleteNote(note)}
          >
            Delete
          </Button>
        </Space>
      ),
    },
  ];

  const errorMessage = getErrorMessage(
    subscriptionError,
    "Failed to load PRISM calendar subscriptions.",
  );

  const renderEventTypeTag = (event, short = false) => (
    <Tag className={`prism-calendar-type-tag prism-calendar-type-tag--${getEventTypeClass(event.types)}`}>
      {getEventTypeLabel(event.types, short)}
    </Tag>
  );

  const openSubscriptionDetails = (subscription, interactionEvent = null) => {
    interactionEvent?.stopPropagation();
    setDetailsSubscription(subscription);
  };

  const handleSubscriptionKeyDown = (interactionEvent, subscription) => {
    if (interactionEvent.key !== "Enter" && interactionEvent.key !== " ") {
      return;
    }

    interactionEvent.preventDefault();
    openSubscriptionDetails(subscription, interactionEvent);
  };

  const renderCalendarEvent = (event) => {
    const markerColor = getCriticalityColor(event.criticality);
    const tooltip = `${getEventTypeLabel(event.types)}: ${
      event.subscription.subscription_name || "-"
    }`;

    return (
      <Tooltip key={event.key} title={tooltip}>
        <div
          aria-label={`View details for ${
            event.subscription.subscription_name || "unnamed subscription"
          }`}
          className={`prism-calendar-event prism-calendar-event--${getEventTypeClass(event.types)}`}
          onClick={(interactionEvent) =>
            openSubscriptionDetails(event.subscription, interactionEvent)
          }
          onKeyDown={(interactionEvent) =>
            handleSubscriptionKeyDown(interactionEvent, event.subscription)
          }
          role="button"
          tabIndex={0}
        >
          {markerColor && (
            <span
              className="prism-calendar-event__criticality"
              style={{ backgroundColor: markerColor }}
            />
          )}
          <span className="prism-calendar-event__type">
            {getEventTypeLabel(event.types, true)}
          </span>
          <span className="prism-calendar-event__name">
            {event.subscription.subscription_name || "-"}
          </span>
        </div>
      </Tooltip>
    );
  };

  const renderCalendarNote = (note) => {
    const colorClassName = getNoteColorClassName(note.color);

    return (
      <Tooltip key={`note-${note.id}`} title={`Note: ${note.title}`}>
        <div className={`prism-calendar-note ${colorClassName}`}>
          <span className="prism-calendar-note__type">Note</span>
          <span className="prism-calendar-note__name">{note.title}</span>
        </div>
      </Tooltip>
    );
  };

  const renderCalendarItem = (item) =>
    item.kind === "note"
      ? renderCalendarNote(item.note)
      : renderCalendarEvent(item.event);

  const renderDateCell = (date) => {
    if (!date.isSame(currentCalendarDate, "month")) return null;

    const dateKey = date.format("YYYY-MM-DD");
    const events = sortCalendarEvents(calendarEventsByDate[dateKey] || []);
    const notes = calendarNotesByDate[dateKey] || [];
    const { items, visibleItems, hiddenItemCount } = getCalendarDateCellSummary(
      notes,
      events,
      visibleItemLimit,
    );

    if (items.length === 0) return null;

    return (
      <div className="prism-calendar-date-events">
        {visibleItems.map(renderCalendarItem)}
        {hiddenItemCount > 0 && (
          <Text className="prism-calendar-more-events">
            +{hiddenItemCount} more - select date
          </Text>
        )}
      </div>
    );
  };

  const renderCalendarCell = (current, info) => {
    if (info.type === "date") return renderDateCell(current);
    return info.originNode;
  };

  const renderSelectedDayPanel = () => (
    <Card
      className="prism-calendar-day-panel"
      title={currentCalendarDate.format("MMMM D, YYYY")}
      extra={
        <Space size={6}>
          <Tag>{selectedDateItems.length} item(s)</Tag>
          {canViewPrivilegedDetails && (
            <Button
              ref={addNoteButtonRef}
              size="small"
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleAddNoteOpen}
            >
              Add General Note
            </Button>
          )}
        </Space>
      }
    >
      {selectedDateItems.length === 0 ? (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description="No subscription events or notes for this date."
        />
      ) : (
        <List
          className="prism-calendar-day-list"
          dataSource={selectedDateItems}
          renderItem={(item) => {
            if (item.kind === "note") {
              const note = item.note;
              const colorClassName = getNoteColorClassName(note.color);

              return (
                <List.Item key={item.key}>
                  <div
                    className={`prism-calendar-day-note ${colorClassName}`}
                  >
                    <Space
                      className="prism-calendar-day-note__header"
                      size={6}
                      wrap
                    >
                      <Tag
                        className={`prism-calendar-note-tag ${colorClassName}`}
                      >
                        Note
                      </Tag>
                      <Title level={5} className="prism-calendar-day-note__title">
                        {note.title}
                      </Title>
                    </Space>
                    {note.note && (
                      <Text className="prism-calendar-day-note__body">
                        {note.note}
                      </Text>
                    )}
                    <Space size={4}>
                      <Button
                        type="link"
                        size="small"
                        icon={<EditOutlined />}
                        onClick={() => handleEditNoteOpen(note)}
                      >
                        Edit
                      </Button>
                      <Button
                        type="link"
                        size="small"
                        danger
                        icon={<DeleteOutlined />}
                        loading={deleteCalendarNoteLoading}
                        onClick={() => handleDeleteNote(note)}
                      >
                        Delete
                      </Button>
                    </Space>
                  </div>
                </List.Item>
              );
            }

            const event = item.event;
            const subscription = event.subscription;

            return (
              <List.Item key={item.key}>
                <div
                  aria-label={`View details for ${
                    subscription.subscription_name || "unnamed subscription"
                  }`}
                  className="prism-calendar-day-event prism-calendar-day-event--interactive"
                  onClick={(interactionEvent) =>
                    openSubscriptionDetails(subscription, interactionEvent)
                  }
                  onKeyDown={(interactionEvent) =>
                    handleSubscriptionKeyDown(interactionEvent, subscription)
                  }
                  role="button"
                  tabIndex={0}
                >
                  <Space size={6} wrap>
                    {renderEventTypeTag(event)}
                    {event.isFullRecord && renderStatus(subscription.contract_status)}
                    {event.isFullRecord && renderCriticality(event.criticality)}
                  </Space>
                  <Tooltip title={subscription.subscription_name || "-"}>
                    <Title level={5} className="prism-calendar-day-event__name">
                      {subscription.subscription_name || "-"}
                    </Title>
                  </Tooltip>
                  <Text className="prism-calendar-day-event__meta">
                    {formatOfficeName(subscription.subscriber_department)}
                    {subscription.subscriber_sub_department
                      ? ` / ${formatSubOfficeName(
                          subscription.subscriber_sub_department,
                        )}`
                      : ""}
                  </Text>
                  <div className="prism-calendar-day-event__details">
                    <Text type="secondary">Date</Text>
                    <Text>{formatDate(event.dateKey)}</Text>
                    <Text type="secondary">Renewal frequency</Text>
                    <Text>
                      {formatLabel(subscription.contract_renewal_frequency)}
                    </Text>
                  </div>
                </div>
              </List.Item>
            );
          }}
        />
      )}
    </Card>
  );

  return (
    <Card
      className={`prism-calendar-shell prism-calendar-shell--${viewMode}`}
      size="small"
    >
      {canViewPrivilegedDetails && (
        <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 4 }}>
          <Tooltip title="Take a tour of this page">
            <Button
              icon={<QuestionCircleOutlined />}
              onClick={() => setIsTourOpen(true)}
              size="small"
            />
          </Tooltip>
        </div>
      )}
      <div className="prism-calendar-toolbar" ref={toolbarRef}>
        {viewMode === "month" ? (
          <Space className="prism-calendar-toolbar__navigation" size={8}>
            <Button
              aria-label="Previous month"
              icon={<LeftOutlined />}
              onClick={() =>
                setCurrentCalendarDate((current) =>
                  current.subtract(1, "month"),
                )
              }
            />
            <Button onClick={() => setCurrentCalendarDate(dayjs())}>Today</Button>
            <Button
              aria-label="Next month"
              icon={<RightOutlined />}
              onClick={() =>
                setCurrentCalendarDate((current) => current.add(1, "month"))
              }
            />
            <DatePicker
              allowClear={false}
              aria-label="Select calendar month and year"
              className="prism-calendar-period-picker"
              format="MMMM YYYY"
              onChange={(selectedMonth) =>
                setCurrentCalendarDate((current) =>
                  setCalendarMonthAndYear(current, selectedMonth),
                )
              }
              picker="month"
              value={currentCalendarDate}
            />
          </Space>
        ) : (
          <Title level={4} className="prism-calendar-toolbar__month">
            Subscription Schedule
          </Title>
        )}

        <div className="prism-calendar-toolbar__controls">
          <Select
            allowClear
            showSearch
            className="prism-calendar-office-filter"
            placeholder="All Offices"
            value={officeFilter}
            options={officeFilterOptions}
            onChange={(value) => setOfficeFilter(value || null)}
            filterOption={(input, option) =>
              (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
            }
          />
          {canViewPrivilegedDetails && (
            <Select
              allowClear
              className="prism-calendar-status-filter"
              placeholder="All Statuses"
              value={statusFilter}
              options={STATUS_FILTER_OPTIONS}
              onChange={(value) => setStatusFilter(value || null)}
            />
          )}
          {viewMode === "month" && (
            <Select
              allowClear
              className="prism-calendar-event-filter"
              placeholder="All Event Types"
              value={eventTypeFilter}
              options={EVENT_TYPE_OPTIONS}
              onChange={(value) => setEventTypeFilter(value || null)}
            />
          )}
          <Radio.Group
            optionType="button"
            buttonStyle="solid"
            value={viewMode}
            onChange={(event) => setViewMode(event.target.value)}
          >
            <Radio.Button value="month">Month View</Radio.Button>
            <Radio.Button value="list">List View</Radio.Button>
          </Radio.Group>
        </div>
      </div>

      {viewMode === "month" && (
        <div className="prism-calendar-summary-row">
          <Space
            className="prism-calendar-summary-row__counts"
            size={[8, 6]}
            wrap
          >
            <Tag color="green">{monthSummary.subscriptions} subscriptions</Tag>
            <Tag color="blue">{monthSummary.renewals} renewals</Tag>
            <Tag color="orange">{monthSummary.endings} contract endings</Tag>
            {canViewPrivilegedDetails && (
              <Tag color="purple">{monthSummary.notes} notes</Tag>
            )}
          </Space>
          <PrismCalendarLegend
            showCriticality={canViewPrivilegedDetails}
            showNotes={canViewPrivilegedDetails}
          />
        </div>
      )}

      {subscriptionError && (
        <Alert
          type="error"
          showIcon
          message="Unable to load calendar"
          description={
            typeof errorMessage === "string"
              ? errorMessage
              : "Please try again later."
          }
          style={{ marginBottom: 12 }}
        />
      )}

      {canViewPrivilegedDetails && calendarNoteError && (
        <Alert
          type="error"
          showIcon
          message="Unable to load general notes"
          description={getErrorMessage(
            calendarNoteError,
            "Please try again later.",
          )}
          style={{ marginBottom: 12 }}
        />
      )}

      {viewMode === "month" ? (
        <div ref={calendarGridRef}>
        <Spin
          className="prism-calendar-month-region"
          spinning={subscriptionLoading || calendarNoteLoading}
        >
          <Row className="prism-calendar-layout" gutter={12}>
            <Col className="prism-calendar-grid-column" span={18}>
              <div
                className={`prism-calendar-grid-viewport prism-calendar-density--${calendarDensity} prism-calendar-weeks--${calendarWeekCount}`}
                data-calendar-density={calendarDensity}
                ref={calendarContainerRef}
              >
                <Calendar
                  className="prism-calendar-month"
                  cellRender={renderCalendarCell}
                  headerRender={() => null}
                  onPanelChange={(value) => setCurrentCalendarDate(value)}
                  onSelect={(value) => setCurrentCalendarDate(value)}
                  value={currentCalendarDate}
                />
              </div>
            </Col>
            <Col className="prism-calendar-panel-column" span={6}>
              {renderSelectedDayPanel()}
            </Col>
          </Row>
        </Spin>
        </div>
      ) : (
        <div className="prism-calendar-list-view">
          <PvTable
            className="prism-calendar-subscriptions-table"
            data={tableData}
            columns={columns}
            loading={subscriptionLoading}
            reverse={false}
            tableLayout="fixed"
            getRowAriaLabel={(record) =>
              `View details for ${
                record.subscription_name || "unnamed subscription"
              }`
            }
            interactiveRows
            onRowClick={setDetailsSubscription}
          />
          {canViewPrivilegedDetails && (
            <Card
              className="prism-calendar-notes-list"
              title="Your General Notes"
              size="small"
              loading={calendarNoteLoading}
            >
              <PvTable
                className="prism-calendar-notes-table"
                data={calendarNotes}
                columns={calendarNoteColumns}
                loading={calendarNoteLoading}
                reverse={false}
                tableLayout="fixed"
                onRowClick={() => {}}
              />
            </Card>
          )}
        </div>
      )}

      <PrismSubscriptionDetailsModal
        details={details}
        onClose={() => setDetailsSubscription(null)}
        open={Boolean(detailsSubscription)}
        subscription={detailsSubscription}
      />

      <Popup
        title={selectedNote ? "Edit General Note" : "Add General Note"}
        isOpen={isNoteModalOpen}
        footer={null}
        width={560}
        onCancel={handleNoteCancel}
      >
        {noteSubmitError && (
          <Alert
            type="error"
            showIcon
            message={selectedNote ? "Unable to update general note" : "Unable to add general note"}
            description={getErrorMessage(
              noteSubmitError,
              selectedNote
                ? "Failed to update general note."
                : "Failed to add general note.",
            )}
            style={{ marginBottom: 12 }}
          />
        )}
        <Form
          form={noteForm}
          layout="vertical"
          onFinish={handleNoteSubmit}
        >
          <Form.Item
            name="note_date"
            label="Date"
            rules={[{ required: true, message: "Please select a date." }]}
          >
            <DatePicker format="YYYY-MM-DD" style={{ width: "100%" }} />
          </Form.Item>
          <Form.Item
            name="title"
            label="Title"
            rules={[
              { required: true, message: "Please enter a note title." },
              { max: 160, message: "Title must be 160 characters or fewer." },
            ]}
          >
            <Input maxLength={160} />
          </Form.Item>
          <Form.Item
            name="note"
            label="Note"
            rules={[{ max: 5000, message: "Note must be 5,000 characters or fewer." }]}
          >
            <Input.TextArea rows={5} maxLength={5000} showCount />
          </Form.Item>
          <Form.Item
            name="color"
            label="Note Color"
            rules={[{ required: true, message: "Please select a note color." }]}
          >
            <Select
              options={NOTE_COLOR_OPTIONS}
              optionRender={(option) => (
                <span className="prism-calendar-note-color-option">
                  <span
                    aria-hidden="true"
                    className={`prism-calendar-note-color-swatch ${getNoteColorClassName(option.value)}`}
                  />
                  {option.label}
                </span>
              )}
            />
          </Form.Item>
          <Form.Item>
            <Button
              type="primary"
              htmlType="submit"
              loading={
                createCalendarNoteLoading || updateCalendarNoteLoading
              }
            >
              {selectedNote ? "Save Changes" : "Add General Note"}
            </Button>
          </Form.Item>
        </Form>
      </Popup>

      {canViewPrivilegedDetails && (
        <Tour open={isTourOpen} onClose={handleTourClose} steps={tourSteps} />
      )}
    </Card>
  );
};

export default PrismCalendar;
